import fetchWrapper from '../fetchWrapper';

const BASE_URL = process.env.BASE_URL;

export interface ConversationResponse {
    id: number;
    blocked: boolean;
    conversationable_type: 'product' | 'post' | 'challenge' | 'direct';
    conversationable_id: number;
    created_at: string;
    updated_at: string;
    last_message?: {
        id: number;
        content: string;
        created_at: string;
        sender?: {
            id: number;
            nickname: string;
            avatar_url: string;
        };
    } | null;
    unread_count?: number;
    participants: {
        id: number;
        user_id: number;
        conversation_id: number;
        joined_at: string;
        left_at: string | null;
        user: {
            id: number;
            nickname: string;
            avatar_url: string;
            avatar?: {
                avatar_og_url: string | null;
                avatar_lg_url: string | null;
                avatar_md_url: string | null;
                avatar_sm_url: string | null;
            };
        };
    }[];
}

export interface MessageResponse {
    id: number;
    conversation_id: number;
    content: string;
    has_media: boolean;
    created_at: string;
    sender: {
        id: number;
        nickname: string;
        avatar_url: string;
    };
    medias: {
        id: number;
        media_url: string;
        media_type: string;
        order: number;
    }[];
}

export interface MessagesListResponse {
    data: MessageResponse[];
    current: number | null;
    total: number;
}

class ChatService {
    /**
     * Get or create a conversation for a specific entity (product, post, challenge, or direct message)
     * @param type - The type of conversation: 'product', 'post', 'challenge', or 'direct'
     * @param participantId - The user ID to chat with (owner of the product/post/challenge)
     * @param entityId - The ID of the product/post/challenge (optional for direct messages)
     * @returns Conversation data with participants
     */
    async getOrCreateConversation(
        type: 'product' | 'post' | 'challenge' | 'direct',
        participantId: number,
        entityId?: number
    ): Promise<ConversationResponse> {
        try {
            const payload: any = {
                type: type,
                participant_id: participantId,
            };

            // For product/post/challenge type, include entity_id
            if (type === 'product' || type === 'post' || type === 'challenge') {
                if (!entityId) {
                    throw new Error('entity_id is required for product/post/challenge conversations');
                }
                payload.entity_id = entityId;
            }

            const response = await fetchWrapper.post(`${BASE_URL}/chat/conversations`, payload);
            return response.data.data;
        } catch (error) {
            console.error('Error getting or creating conversation:', error);
            throw error;
        }
    }

    /**
     * Get messages for a specific conversation with pagination
     * @param conversationId - The conversation ID
     * @param page - Page number for pagination (optional)
     * @param perPage - Number of messages per page (default: 20)
     * @returns List of messages with pagination info
     */
    async getConversationMessages(
        conversationId: number,
        page: number = 1,
        perPage: number = 20
    ): Promise<MessagesListResponse> {
        try {
            const params: any = {
                conversation_id: conversationId,
                per_page: perPage,
                page: page,
            };

            const response = (await fetchWrapper.get(`${BASE_URL}/chat/conversations/messages`, { params })) as any;
            // fetchWrapper already extracts the full response, so response.data is the messages array
            return {
                data: response.data,
                current: response.current,
                total: response.total
            };
        } catch (error) {
            console.error('Error fetching conversation messages:', error);
            throw error;
        }
    }

    /**
     * Send a message in a conversation
     * @param conversationId - The conversation ID
     * @param content - Message text content
     * @param formData - FormData containing conversation_id, content, and media files
     * @returns The created message
     */
    async sendMessage(
        conversationId: number,
        content: string,
        formData: FormData
    ): Promise<MessageResponse> {
        try {
            const response = await fetchWrapper.post(`${BASE_URL}/chat/messages`, formData);

            // Handle case where response is undefined (request canceled/failed)
            if (!response) {
                throw new Error('Request failed: No response received');
            }

            if (!response.data) {
                throw new Error('Request failed: No data in response');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    }

    /**
     * Mark a conversation as read
     * @param conversationId - The conversation ID to mark as read
     */
    async markConversationAsRead(conversationId: number): Promise<void> {
        try {
            await fetchWrapper.post(`${BASE_URL}/chat/conversations/read/${conversationId}`);
        } catch (error) {
            console.error('Error marking conversation as read:', error);
            throw error;
        }
    }

    /**
     * Add participants to a conversation (for group chats)
     * @param conversationId - The conversation ID
     * @param userIds - Array of user IDs to add
     */
    async addParticipants(conversationId: number, userIds: number[]): Promise<void> {
        try {
            await fetchWrapper.post(`${BASE_URL}/chat/conversations/add/participants`, {
                conversation_id: conversationId,
                user_ids: userIds,
            });
        } catch (error) {
            console.error('Error adding participants:', error);
            throw error;
        }
    }

    /**
     * Remove participants from a conversation
     * @param conversationId - The conversation ID
     * @param userIds - Array of user IDs to remove
     */
    async removeParticipants(conversationId: number, userIds: number[]): Promise<void> {
        try {
            await fetchWrapper.post(`${BASE_URL}/chat/conversations/remove/participants`, {
                conversation_id: conversationId,
                user_ids: userIds,
            });
        } catch (error) {
            console.error('Error removing participants:', error);
            throw error;
        }
    }

    /**
     * Get all conversations for the current user
     * @returns List of conversations
     */
    async getConversations(): Promise<ConversationResponse[]> {
        try {
            const response = await fetchWrapper.get(`${BASE_URL}/chat/conversations`);
            return response.data;
        } catch (error) {
            console.error('Error fetching conversations:', error);
            throw error;
        }
    }

     /**
     * Get all conversations for the current user
     * @returns List of conversations
     */
    async getConversation(conversationId: number): Promise<ConversationResponse[]> {
        try {
            const response = await fetchWrapper.get(`${BASE_URL}/chat/conversations/${conversationId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching conversations:', error);
            throw error;
        }
    }

    /**
     * Get unread messages count for the user
     * @param conversationId - Optional conversation ID to get unread count for a specific room
     */
    async getUnreadCount(conversationId?: number): Promise<any> {
        try {
            const params = conversationId ? { conversation_id: conversationId } : {};
            const response = await fetchWrapper.get(`${BASE_URL}/chat/unread-count`, { params });
            return response;
        } catch (error) {
            console.error('Error getting unread count:', error);
            throw error;
        }
    }

    /**
     * Update active status for notification suppression
     * @param conversationId - The conversation ID the user is currently viewing
     */
    async updateActiveStatus(conversationId: number): Promise<void> {
        try {
            await fetchWrapper.post(`${BASE_URL}/chat/active-status`, {
                conversation_id: conversationId,
            });
        } catch (error) {
            console.error('Error updating active status:', error);
        }
    }

    /**
     * Clear active status when leaving chat
     */
    async clearActiveStatus(): Promise<void> {
        try {
            await fetchWrapper.delete(`${BASE_URL}/chat/active-status`);
        } catch (error) {
            console.error('Error clearing active status:', error);
        }
    }
}

const chatService = new ChatService();
export default chatService;
